
import React from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Sparkles } from 'lucide-react';

interface ContentGenerationFormProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  contentType: string;
  setContentType: (type: string) => void;
  isGenerating: boolean;
  onGenerate: () => void;
}

interface PromptSuggestions {
  announcement: string[];
  marketing: string[];
  notification: string[];
  [key: string]: string[];
}

const ContentGenerationForm: React.FC<ContentGenerationFormProps> = ({
  prompt,
  setPrompt,
  contentType,
  setContentType,
  isGenerating,
  onGenerate
}) => {
  const promptSuggestions: PromptSuggestions = {
    announcement: [
      "Announce our new summer camp program for ages 5-12",
      "Inform parents about upcoming recital schedule changes",
      "Announce a special discount for siblings enrolling in the same class"
    ],
    marketing: [
      "Create marketing copy for our beginner ballet class focusing on building confidence",
      "Write promotional text for our advanced hip-hop program for teenagers",
      "Create an email campaign for our early registration discount"
    ],
    notification: [
      "Reminder about the upcoming parent-teacher meetings",
      "Notify parents about the spring break schedule",
      "Important information about the new location"
    ]
  };

  const usePromptSuggestion = (suggestion: string) => {
    setPrompt(suggestion);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Generate New Content</CardTitle>
        <CardDescription>
          Use AI to create announcements, marketing copy, or notifications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="content-type">Content Type</Label>
          <Select value={contentType} onValueChange={setContentType}>
            <SelectTrigger id="content-type">
              <SelectValue placeholder="Select content type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="announcement">Announcement</SelectItem>
              <SelectItem value="marketing">Marketing Copy</SelectItem>
              <SelectItem value="notification">Notification</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="prompt">Prompt</Label>
          <Textarea
            id="prompt"
            placeholder="Describe what you want to create..."
            className="min-h-[100px]"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>Suggestions</Label>
          <div className="flex flex-wrap gap-2">
            {promptSuggestions[contentType]?.map((suggestion, index) => (
              <Button 
                key={index} 
                variant="outline" 
                size="sm" 
                onClick={() => usePromptSuggestion(suggestion)}
                className="text-xs"
              >
                {suggestion}
              </Button>
            ))}
          </div>
        </div>

        <div className="pt-4">
          <Button 
            onClick={onGenerate} 
            disabled={isGenerating || !prompt} 
            className="w-full flex items-center gap-2"
          >
            <Sparkles className="h-4 w-4" />
            {isGenerating ? 'Generating...' : 'Generate Content'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContentGenerationForm;
